import { useApi } from '@hero/context';
import type {
  UseAgreementCreateApi,
  UseAgreementGetOneApi,
  UseAgreementListApi,
  UseAgreementUpdateApi,
} from './types';
import { useInfiniteQueryAPI, useMutationAPI, useQueryAPI } from '../../utils';

/**
 * Get one Agreement by `_id`. Disabled while `id` is undefined.
 */
export const useAgreementApi: UseAgreementGetOneApi = (id, apiConfig) => {
  const api = useApi();
  return useQueryAPI({
    entity: 'agreement',
    apiFn: api.agreements.getOne,
    singleIdQuery: id,
    query: undefined,
    apiConfig,
    isEnabled: () => !!id,
  });
};

/**
 * Paginated Agreement list. Filter by `orgId`, `status`, `type`.
 *
 * Hook API: {@link UseAgreementListApi}
 */
export const useAgreementListApi: UseAgreementListApi = (query, apiConfig) => {
  const api = useApi();
  return useInfiniteQueryAPI({
    entity: 'agreement',
    apiFn: api.agreements.getMany,
    query,
    apiConfig,
  });
};

/**
 * Create Agreement. After insertion, the caller is expected to push Routes
 * via `useRouteCreateApi` with the resulting `agreementId`.
 *
 * Hook API: {@link UseAgreementCreateApi}
 */
export const useAgreementCreateApi: UseAgreementCreateApi = apiConfig => {
  const api = useApi();
  return useMutationAPI({
    entity: 'agreement',
    apiFn: api.agreements.create,
    apiConfig,
  });
};

/**
 * Update Agreement. Status changes (e.g. terminate) cascade-archive linked
 * Routes server-side and the `agreement` entity invalidates `route` caches.
 *
 * Hook API: {@link UseAgreementUpdateApi}
 */
export const useAgreementUpdateApi: UseAgreementUpdateApi = apiConfig => {
  const api = useApi();
  return useMutationAPI({
    entity: 'agreement',
    apiFn: api.agreements.update,
    apiConfig,
  });
};
